import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Mail, Lock, LogIn, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md aldia-fade-up">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#3A6B56] flex items-center justify-center mx-auto">
            <ShieldCheck size={34} className="text-white" />
          </div>
          <p className="font-heading text-sm font-bold uppercase tracking-widest text-[#3A6B56] mt-4">AL DÍA</p>
          <h1 className="font-heading text-3xl font-bold text-[#1C2421] mt-1">Bienvenido de nuevo</h1>
          <p className="text-base text-[#6E7A74] mt-1">Para que no se te pase nada importante.</p>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#E5DFD5] rounded-2xl p-6 shadow-sm space-y-4">
          <Field icon={Mail} label="Correo electrónico" type="email" value={email} onChange={setEmail} placeholder="tucorreo@ejemplo.com" testid="login-email" />
          <Field icon={Lock} label="Contraseña" type="password" value={password} onChange={setPassword} placeholder="Tu contraseña" testid="login-password" autoComplete="current-password" />
          <div className="text-right -mt-1">
            <Link to="/recuperar" data-testid="forgot-password" className="text-sm font-semibold text-[#3A6B56]">¿Olvidaste tu contraseña?</Link>
          </div>
          {error && <p data-testid="login-error" className="text-sm font-semibold text-[#B91C1C] bg-[#FEF2F2] rounded-xl p-3">{error}</p>}
          <button data-testid="login-submit" disabled={busy} className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            <LogIn size={22} /> {busy ? "Entrando…" : "Iniciar sesión"}
          </button>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-[#E5DFD5]" />
            <span className="text-sm text-[#9AA39D]">o</span>
            <div className="flex-1 h-px bg-[#E5DFD5]" />
          </div>

          <button type="button" data-testid="login-google" onClick={googleLogin} className="w-full min-h-[56px] rounded-xl bg-white border-2 border-[#E5DFD5] text-[#1C2421] font-semibold text-base hover:border-[#3A6B56] transition-colors flex items-center justify-center gap-3">
            <GoogleIcon /> Continuar con Google
          </button>
        </form>

        <p className="text-center text-base text-[#6E7A74] mt-6">
          ¿No tienes cuenta?{" "}
          <Link to="/registro" data-testid="go-register" className="font-bold text-[#3A6B56]">Crear una cuenta</Link>
        </p>
      </div>
    </div>
  );
}

export const Field = ({ icon: Icon, label, type, value, onChange, placeholder, testid, autoComplete, invalido }) => {
  const [ver, setVer] = useState(false);
  const esPwd = type === "password";
  return (
    <div>
      <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9AA39D]" />}
        <input
          type={esPwd && ver ? "text" : type}
          value={value}
          data-testid={testid}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={invalido || undefined}
          {...(esPwd ? { autoCapitalize: "none", autoCorrect: "off", spellCheck: false } : {})}
          className={`w-full min-h-[56px] ${Icon ? "pl-12" : "pl-4"} ${esPwd ? "pr-14" : "pr-4"} rounded-xl bg-[#FAF8F5] border ${invalido ? "border-[#B91C1C]" : "border-[#E5DFD5]"} text-base text-[#1C2421] placeholder-[#9AA39D] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/20 focus:border-[#3A6B56]`}
        />
        {esPwd && (
          <button type="button" onClick={() => setVer((v) => !v)} data-testid={`${testid}-ver`}
            aria-label={ver ? "Ocultar contraseña" : "Mostrar contraseña"} aria-pressed={ver}
            className="absolute right-1 top-1/2 -translate-y-1/2 w-12 h-12 rounded-xl flex items-center justify-center text-[#6E7A74] hover:text-[#1C2421]">
            {ver ? <EyeOff size={22} /> : <Eye size={22} />}
          </button>
        )}
      </div>
    </div>
  );
};

// Aviso en tiempo real de si las dos contraseñas coinciden.
export const Coinciden = ({ a, b, testid }) => {
  if (!b) return null;
  const ok = a === b;
  return (
    <p data-testid={testid} role="status" className={`text-sm font-semibold flex items-center gap-1.5 -mt-2 ${ok ? "text-[#2E5645]" : "text-[#B91C1C]"}`}>
      {ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />} {ok ? "Las contraseñas coinciden" : "Las contraseñas no coinciden"}
    </p>
  );
};

export const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.3C41.1 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/>
  </svg>
);
